import { FunctionComponent, useEffect, useRef, useState } from 'react';
import ArrowDropDownFilled from '../../../../assets/images/arrowDropDownFilled.svg';
import styles from './index.module.scss';
import CloseIcon from '../../../../assets/images/icon-Close.svg';
import { STATUS } from '../../constants/index';

interface AdvancedFiltersDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    languageFilter: string;
    onLanguageChange: (language: string) => void;
    createdDateFilter: string;
    onCreatedDateChange: (createdDate: string) => void;
    createdByFilter: string;
    onCreatedByChange: (createdBy: string) => void;
    lastModificationDateFilter?: string;
    onLastModificationDateChange?: (lastModificationDate: string) => void;
    noFoldersFilter?: string;
    onNoFoldersChange?: (noFolders: string) => void;
    relatedSeminarFilter?: string;
    onRelatedSeminarChange?: (relatedSeminar: string) => void;
    relatedTemplateFilter?: string;
    onRelatedTemplateChange?: (relatedTemplate: string) => void;
    statusFilter?: string;
    onStatusChange?: (status: string) => void;
    availableLanguages: string[];
    availableCreatedDates: string[];
    availableCreatedBy: string[];
    availableLastModificationDates?: string[];
    availableNoFolders?: string[];
    availableRelatedSeminars?: string[];
    availableRelatedTemplates?: string[];
    onClearAdvancedFilters: () => void;
    onSaveFilters?: () => void;
}

const AdvancedFiltersDropdown: FunctionComponent<AdvancedFiltersDropdownProps> = ({
    isOpen,
    onClose,
    languageFilter,
    onLanguageChange,
    createdDateFilter,
    onCreatedDateChange,
    createdByFilter,
    onCreatedByChange,
    lastModificationDateFilter = "All",
    onLastModificationDateChange,
    noFoldersFilter = "All",
    onNoFoldersChange,
    relatedSeminarFilter = "All",
    onRelatedSeminarChange,
    relatedTemplateFilter = "All",
    onRelatedTemplateChange,
    statusFilter = "All",
    onStatusChange,
    availableLanguages,
    availableCreatedDates,
    availableCreatedBy,
    availableLastModificationDates = [],
    availableNoFolders = [],
    availableRelatedSeminars = [],
    availableRelatedTemplates = [],
    onClearAdvancedFilters,
    onSaveFilters
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // Local state for temporary filter values
    const [tempLanguageFilter, setTempLanguageFilter] = useState(languageFilter);
    const [tempCreatedDateFilter, setTempCreatedDateFilter] = useState(createdDateFilter);
    const [tempCreatedByFilter, setTempCreatedByFilter] = useState(createdByFilter);
    const [tempLastModificationDateFilter, setTempLastModificationDateFilter] = useState(lastModificationDateFilter);
    const [tempNoFoldersFilter, setTempNoFoldersFilter] = useState(noFoldersFilter);
    const [tempRelatedSeminarFilter, setTempRelatedSeminarFilter] = useState(relatedSeminarFilter);
    const [tempRelatedTemplateFilter, setTempRelatedTemplateFilter] = useState(relatedTemplateFilter);
    const [tempStatusFilter, setTempStatusFilter] = useState(statusFilter);

    // Update local state when props change (when the dropdown opens)
    useEffect(() => {
        if (isOpen) {
            setTempLanguageFilter(languageFilter);
            setTempCreatedDateFilter(createdDateFilter);
            setTempCreatedByFilter(createdByFilter);
            setTempLastModificationDateFilter(lastModificationDateFilter);
            setTempNoFoldersFilter(noFoldersFilter);
            setTempRelatedSeminarFilter(relatedSeminarFilter);
            setTempRelatedTemplateFilter(relatedTemplateFilter);
            setTempStatusFilter(statusFilter);
        }
    }, [isOpen, languageFilter, createdDateFilter, createdByFilter, lastModificationDateFilter, noFoldersFilter, relatedSeminarFilter, relatedTemplateFilter, statusFilter]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleCancel = () => {
        onClose();
    };

    const handleSave = () => {
        // Apply all filters when Save is clicked
        onLanguageChange(tempLanguageFilter);
        onCreatedDateChange(tempCreatedDateFilter);
        onCreatedByChange(tempCreatedByFilter);
        if (onLastModificationDateChange) {
            onLastModificationDateChange(tempLastModificationDateFilter);
        }
        if (onNoFoldersChange) {
            onNoFoldersChange(tempNoFoldersFilter);
        }
        if (onRelatedSeminarChange) {
            onRelatedSeminarChange(tempRelatedSeminarFilter);
        }
        if (onRelatedTemplateChange) {
            onRelatedTemplateChange(tempRelatedTemplateFilter);
        }
        if (onStatusChange) {
            onStatusChange(tempStatusFilter);
        }
        
        if (onSaveFilters) {
            onSaveFilters();
        }
        onClose();
    };

    return (
        <div className={styles.filtersDropdown} ref={dropdownRef}>
            <img className={styles.icnCloseIcon} alt="" src={CloseIcon} onClick={onClose} />
            <div className={styles.header}>
                <div className={styles.title}>Filters</div>
            </div>

            <div className={styles.form}>
                {onStatusChange && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Status</label>
                            <select
                                value={tempStatusFilter}
                                onChange={(e) => setTempStatusFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {Object.values(STATUS).map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableLanguages.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Language</label>
                            <select
                                value={tempLanguageFilter}
                                onChange={(e) => setTempLanguageFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableLanguages.map((language) => (
                                    <option key={language} value={language}>{language}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableCreatedDates.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Created Date</label>
                            <select
                                value={tempCreatedDateFilter}
                                onChange={(e) => setTempCreatedDateFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableCreatedDates.map((date) => (
                                    <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableCreatedBy.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Created By</label>
                            <select
                                value={tempCreatedByFilter}
                                onChange={(e) => setTempCreatedByFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableCreatedBy.map((creator) => (
                                    <option key={creator} value={creator}>{creator}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableLastModificationDates.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Last Modification Date</label>
                            <select
                                value={tempLastModificationDateFilter}
                                onChange={(e) => setTempLastModificationDateFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableLastModificationDates.map((date) => (
                                    <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableNoFolders.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>No. Folders</label>
                            <select
                                value={tempNoFoldersFilter}
                                onChange={(e) => setTempNoFoldersFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableNoFolders.map((folderCount) => (
                                    <option key={folderCount} value={folderCount}>{folderCount}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableRelatedSeminars.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Related Seminar</label>
                            <select
                                value={tempRelatedSeminarFilter}
                                onChange={(e) => setTempRelatedSeminarFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableRelatedSeminars.map((seminar) => (
                                    <option key={seminar} value={seminar}>{seminar}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}

                {availableRelatedTemplates.length > 0 && (
                    <div className={styles.filterGroup}>
                        <div className={styles.selectContainer}>
                            <label className={styles.label}>Related Template</label>
                            <select
                                value={tempRelatedTemplateFilter}
                                onChange={(e) => setTempRelatedTemplateFilter(e.target.value)}
                                className={styles.select}
                            >
                                <option value="All">All</option>
                                {availableRelatedTemplates.map((template) => (
                                    <option key={template} value={template}>{template}</option>
                                ))}
                            </select>
                            <img className={styles.arrowIcon} alt="" src={ArrowDropDownFilled} />
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.buttons}>
                <button className={styles.cancelButton} onClick={handleCancel}>
                    Cancel
                </button>
                <button className={styles.saveButton} onClick={handleSave}>
                    Apply
                </button>
            </div>
        </div>
    );
};

export default AdvancedFiltersDropdown; 